/* eslint-disable i18next/no-literal-string */
import React, { useCallback } from "react";
import styles from "./select.module.scss";
import classnames from "classnames";
import { useFormikContext } from "formik";
import type { FormSelectProps } from "@whatslly/types/UI-V2/FormElements/FormSelectType";
import { Label } from "../../BaseElements/Label";
import globalStyles from "../../globalStyles";
import { Select } from "../../BaseElements/Select";
import type { SelectOption } from "@whatslly/types/UI-V2/BaseElements/SelectType";
import { ActionMeta } from "react-select";
import { FormFieldV2 } from "../FormField";

export const FormSelect = <
  OptionValueType,
  IsMulti extends boolean = false,
  Option extends SelectOption<OptionValueType> = SelectOption<OptionValueType>,
>({
  labelText,
  labelFontSize,
  labelFontWeight,
  labelColor,
  labelUnderLine,
  labelInline,
  padding,
  paddingInline,
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,
  margin,
  marginTop,
  marginLeft,
  marginBottom,
  marginRight,
  options,
  onBlur,
  labelComment,
  className,
  onChange,
  name,
  required,
  ignoreMenuDirection,
  ...props
}: FormSelectProps<OptionValueType, IsMulti, Option>) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks, @typescript-eslint/no-explicit-any
  const formikContext = useFormikContext<Record<string, any>>();
  const formOnChange = useCallback(
    (newValue: IsMulti extends false ? Option : Option[], actionMeta: ActionMeta<Option>) => {
      formikContext.setFieldValue(name, newValue, true);
      if (onChange) {
        return onChange(newValue, actionMeta);
      }
    },
    [formikContext, name, onChange]
  );
  return (
    <FormFieldV2
      className={classnames(
        styles.selectContainer,
        globalStyles[`p-${padding}`],
        globalStyles[`pi-${paddingInline ?? "default"}`],
        globalStyles[`pt-${paddingTop}`],
        globalStyles[`pr-${paddingRight}`],
        globalStyles[`pb-${paddingBottom}`],
        globalStyles[`pl-${paddingLeft}`],
        globalStyles[`m-${margin}`],
        globalStyles[`mt-${marginTop}`],
        globalStyles[`mr-${marginRight}`],
        globalStyles[`mb-${marginBottom ?? "default"}`],
        globalStyles[`ml-${marginLeft}`],
        globalStyles[`d-${labelInline ? "flex" : ""}`],
        globalStyles[`flex-${labelInline ? "row" : ""}`],
        globalStyles[`align-items-${labelInline ? "center" : ""}`],
        globalStyles[`justify-content-${labelInline ? "between" : ""}`]
      )}
      errorText={formikContext.errors[name] as string}
      showError={!!formikContext.errors[name] && !!formikContext.touched[name]}
    >
      {labelText && (
        <Label
          text={labelText}
          fontSize={labelFontSize}
          color={labelColor}
          marginBottom={labelInline ? "none" : "small"}
          underLine={labelUnderLine}
          comment={labelComment}
          fontWeight={labelFontWeight}
          required={required}
          className={styles.formSelectLabel}
        />
      )}
      <Select<OptionValueType, IsMulti, Option>
        ignoreMenuDirection={ignoreMenuDirection}
        placeholder={props.placeholder}
        onChange={formOnChange}
        name={name}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          if (onBlur) {
            onBlur(e);
          }
        }}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        options={options as any}
        className={classnames(
          styles.FormtextInput,
          globalStyles["v2ElementContainer"],
          {
            [styles.inlineLabel]: labelInline,
          },
          className
        )}
        {...props}
      />
    </FormFieldV2>
  );
};
